import { Menu, Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useUnassignedEvents } from "@/features/today/hooks";
import { useAuth } from "@/lib/auth";
import { useIsDesktop } from "@/lib/use-media";
import { cn } from "@/lib/utils";
import { CommandMenu } from "./CommandMenu";
import { Sidebar } from "./Sidebar";

const COLLAPSE_KEY = "optitime.sidebar.collapsed";

export function AppShell() {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const isDesktop = useIsDesktop();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const unassigned = useUnassignedEvents({ enabled: hasRole("admin") });
  const badges = { unassigned: unassigned.data?.length ?? 0 };

  const toggle = () => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, c ? "0" : "1");
      } catch {
        /* nur Komfort */
      }
      return !c;
    });
  };

  return (
    <div className="min-h-dvh bg-background">
      {isDesktop && (
        <aside className={cn("fixed inset-y-0 left-0 z-30 border-r border-sidebar-border transition-[width]", collapsed ? "w-16" : "w-60")}>
          <Sidebar collapsed={collapsed} onToggle={toggle} badges={badges} />
        </aside>
      )}
      {!isDesktop && (
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="w-72 p-0" showCloseButton={false}>
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <Sidebar onNavigate={() => setMobileOpen(false)} badges={badges} />
          </SheetContent>
        </Sheet>
      )}
      <div className={cn("flex min-h-dvh flex-col", isDesktop && (collapsed ? "pl-16" : "pl-60"))}>
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b bg-background/85 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/70 lg:px-8">
          {!isDesktop && (
            <button onClick={() => setMobileOpen(true)} className="-ml-1 rounded-md p-1.5 hover:bg-accent" aria-label="Navigation öffnen">
              <Menu className="size-5" />
            </button>
          )}
          <button
            onClick={() => setSearchOpen(true)}
            className="flex h-9 w-full max-w-sm items-center gap-2 rounded-lg border bg-muted/40 px-3 text-left text-[13px] text-muted-foreground hover:bg-muted"
          >
            <Search className="size-4" />
            <span className="flex-1 truncate">{t("nav.search")}</span>
            <kbd className="hidden rounded border bg-background px-1.5 font-sans text-[11px] sm:inline">⌘K</kbd>
          </button>
        </header>
        <main className="safe-bottom flex-1 px-4 py-6 lg:px-8 lg:py-8">
          <div className="mx-auto w-full max-w-6xl">
            <Outlet />
          </div>
        </main>
      </div>
      <CommandMenu open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
}
